import { useState } from 'react';
import { fmtDateWeek, cnDate } from '@wd/shared';
import { errorText } from '../../lib/api';
import { useMcpToken, useOAuthGrants } from '../../lib/queries';
import { BottomSheet, Button, Modal } from '../../ui/primitives';

// 本地智能体（MCP）：每人一个令牌，Claude Code、Codex、WorkBuddy、OpenCode、Cursor 等用它以你的身份写日报、查项目。
// 令牌原文只在生成时显示一次。手机、网页版 Claude 填不了令牌，走连接器（OAuth）登录授权，见 ConnectorSection。

interface Client {
  id: string;
  name: string;
  /** 放在哪里、怎么生效 */
  where: string;
  code: string;
}

/** 各家智能体的接入配置。令牌放在 Authorization 请求头里；都用 Streamable HTTP 直连，只有 Claude 桌面版要用 mcp-remote 转一下 */
function clients(url: string, token: string): Client[] {
  const auth = `Bearer ${token}`;
  const json = (v: unknown) => JSON.stringify(v, null, 2);
  return [
    {
      id: 'claude-code',
      name: 'Claude Code',
      where: '在终端里运行这一行。',
      code: `claude mcp add --transport http wattsonic-daily ${url} --header "Authorization: ${auth}"`,
    },
    {
      id: 'codex',
      name: 'Codex',
      where: '加到 ~/.codex/config.toml 末尾，保存后重新打开 Codex。',
      code: `[mcp_servers.wattsonic-daily]\nurl = "${url}"\nhttp_headers = { "Authorization" = "${auth}" }`,
    },
    {
      id: 'workbuddy',
      name: 'WorkBuddy',
      where: '连接 › 自定义连接 › 右上角「配置 MCP」，把下面的内容合并进 ~/.workbuddy/mcp.json，保存后在「我的 MCP」里打开开关。',
      code: json({ mcpServers: { 'wattsonic-daily': { url, headers: { Authorization: auth } } } }),
    },
    {
      id: 'opencode',
      name: 'OpenCode',
      where: '合并进 ~/.config/opencode/opencode.json（只给某个项目用就放在项目根目录的 opencode.json）。',
      code: json({ $schema: 'https://opencode.ai/config.json', mcp: { 'wattsonic-daily': { type: 'remote', url, enabled: true, oauth: false, headers: { Authorization: auth } } } }),
    },
    {
      id: 'cursor',
      name: 'Cursor / Windsurf',
      where: 'Cursor：设置 › MCP › Add new MCP server，或合并进 ~/.cursor/mcp.json。Windsurf 合并进 ~/.codeium/windsurf/mcp_config.json。',
      code: json({ mcpServers: { 'wattsonic-daily': { url, headers: { Authorization: auth } } } }),
    },
    {
      id: 'claude-desktop',
      name: 'Claude 桌面版',
      where: '需要先装 Node.js。设置 › 开发者 › 编辑配置，合并进 claude_desktop_config.json，然后重启 Claude。',
      code: json({ mcpServers: { 'wattsonic-daily': { command: 'npx', args: ['-y', 'mcp-remote', url, '--header', 'Authorization:${AUTH}'], env: { AUTH: auth } } } }),
    },
  ];
}

/** 选一个智能体，看它的接入配置 */
function ClientGuide({ url, token }: { url: string; token: string }) {
  const list = clients(url, token);
  const [pick, setPick] = useState(list[0]!.id);
  const c = list.find((x) => x.id === pick) ?? list[0]!;
  return (
    <div className="me-mcp-block">
      <div className="me-mcp-tabs" role="tablist" aria-label="选择智能体">
        {list.map((x) => (
          <button key={x.id} type="button" role="tab" aria-selected={x.id === pick} className={x.id === pick ? 'on' : undefined} onClick={() => setPick(x.id)}>
            {x.name}
          </button>
        ))}
      </div>
      <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
        <span className="t-meta flex1">{c.where}</span>
        <Copy text={c.code} />
      </div>
      <pre className="me-mcp-code">{c.code}</pre>
    </div>
  );
}

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="text"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* 浏览器不让写剪贴板时手动复制 */
        }
      }}
    >
      {done ? '已复制' : '复制'}
    </Button>
  );
}

/** 手机、网页版 Claude：用连接器登录日报授权，不用令牌；已连接的可以逐个断开 */
function ConnectorSection({ url }: { url: string }) {
  const { list, revoke } = useOAuthGrants();
  const [error, setError] = useState<string | null>(null);
  const day = (iso: string) => fmtDateWeek(cnDate(new Date(iso)));
  return (
    <div className="me-mcp-block me-mcp-connector">
      <div className="k">手机和网页版 Claude</div>
      <p className="t-meta">
        不用令牌。在 claude.ai 网页上打开「设置 › 连接器 › 添加自定义连接器」，名称填「Wattsonic 日报」，地址填下面这个。点「连接」后登录日报、点「允许」，手机上的 Claude 会自动同步。
      </p>
      <div className="row" style={{ gap: 8 }}>
        <code className="me-mcp-code flex1">{url}</code>
        <Copy text={url} />
      </div>
      {(list.data ?? []).map((g) => (
        <div key={g.id} className="row me-mcp-grant">
          <span className="flex1">
            {g.clientName} · {day(g.createdAt)} 连接 · {g.lastUsedAt ? `最后使用 ${day(g.lastUsedAt)}` : '还没用过'}
          </span>
          <Button variant="text" disabled={revoke.isPending} onClick={() => revoke.mutate(g.id, { onError: (e) => setError(errorText(e)) })}>
            断开
          </Button>
        </div>
      ))}
      {error && <div className="t-meta c-red">{error}</div>}
    </div>
  );
}

export function McpPanel({ mobile, onClose }: { mobile: boolean; onClose: () => void }) {
  const { info, issue, revoke } = useMcpToken();
  const [token, setToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `${location.origin}/mcp`;
  const t = info.data;

  const doIssue = () => {
    setError(null);
    issue.mutate(undefined, { onSuccess: (r) => setToken(r.token), onError: (e) => setError(errorText(e)) });
  };

  const body = (
    <div className="me-mcp">
      <p className="t-meta" style={{ marginTop: 0 }}>
        让 Claude Code、Codex、WorkBuddy、OpenCode、Cursor 等电脑上的 AI 智能体，或者手机、网页版的 Claude，帮你写日报、查项目进展、处理收件箱。智能体用你的身份操作，能做的事和你登录网页一样；设置、成员管理、数据导出不开放。
      </p>

      {token ? (
        <>
          <div className="me-mcp-warn">令牌只显示这一次，关掉后就看不到了。请马上复制到智能体的配置里，不要发给别人。</div>
          <div className="me-mcp-block">
            <div className="row">
              <span className="k flex1">你的令牌</span>
              <Copy text={token} />
            </div>
            <code className="me-mcp-code">{token}</code>
          </div>
          <ClientGuide url={url} token={token} />
          <p className="t-meta">配好后对智能体说「帮我写今天的日报」「PRJ-07 现在卡在哪」试试。</p>
        </>
      ) : t?.exists ? (
        <>
          <div className="me-mcp-block">
            <div className="k">已开启</div>
            <div className="t-meta">
              令牌末 4 位 {t.hint} · {t.createdAt ? `${fmtDateWeek(cnDate(new Date(t.createdAt)))} 生成` : ''} · {t.lastUsedAt ? `最后使用 ${fmtDateWeek(cnDate(new Date(t.lastUsedAt)))}` : '还没用过'}
            </div>
          </div>
          <p className="t-meta">令牌原文生成时只显示一次。忘了或者换电脑，就重新生成一个，旧的会立即失效。服务地址：{url}</p>
          <ClientGuide url={url} token="<你的令牌>" />
          {confirmRevoke ? (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span className="t-meta flex1">停用后，用这个令牌的智能体马上就连不上了。</span>
              <Button variant="text" onClick={() => setConfirmRevoke(false)}>
                取消
              </Button>
              <Button disabled={revoke.isPending} onClick={() => revoke.mutate(undefined, { onSuccess: () => setConfirmRevoke(false), onError: (e) => setError(errorText(e)) })}>
                确认停用
              </Button>
            </div>
          ) : (
            <div className="row" style={{ gap: 8 }}>
              <Button variant="primary" disabled={issue.isPending} onClick={doIssue}>
                重新生成
              </Button>
              <Button variant="text" onClick={() => setConfirmRevoke(true)}>
                停用
              </Button>
            </div>
          )}
        </>
      ) : (
        <Button variant="primary" disabled={issue.isPending || info.isLoading} onClick={doIssue}>
          生成令牌
        </Button>
      )}
      {error && <div className="t-meta c-red">{error}</div>}
      <ConnectorSection url={url} />
    </div>
  );

  if (mobile) {
    return (
      <BottomSheet onClose={onClose}>
        <div style={{ padding: '4px 20px 12px' }}>
          <div className="t-block-title" style={{ marginBottom: 8 }}>
            本地智能体（MCP）
          </div>
          {body}
        </div>
      </BottomSheet>
    );
  }
  return (
    <Modal width={600} title="本地智能体（MCP）" subtitle="每个账号一个专属令牌" onClose={onClose}>
      {body}
    </Modal>
  );
}
